import { z } from 'zod'

import type {
  AdapterReferenceFilter,
  AdapterSchemaAttributes,
  AdapterSchemaRelation,
} from '../../../engine/contracts'
import { getEntityRegistry } from '../../../engine/entity-registry'
import { getHostRuntime } from '../../../engine/host-runtime'
import {
  andCombineFilters,
  cacheBuiltFilter,
  findFieldsForEnumValue,
  findReadonlyFilterFields,
  mergeSameFieldConditions,
  mergeSameFieldGroups,
  normalizeEnumCondition,
  normalizeValueForOperator,
  pickEditableStatusField,
  savedFilterStructureSchema,
} from '../../filter-normalizer'
import {
  resolveDataClass,
  unknownEntityError,
} from '../../shared/entity-serializer'
import { encodeFilterQuery } from '../../shared/filterQuery'
import {
  FIELD_TYPE_OPERATORS,
  HIDDEN_FILTER_ATTRIBUTES,
  OPERATOR_DEFINITIONS,
} from '@/Atomic/Organisms/FilterBuilder/config'
import type { SavedFilterStructure } from '@/Atomic/Organisms/FilterBuilder/types'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'

type SchemaAttributeMap = AdapterSchemaAttributes | undefined

export const buildFilterTool = defineTool({
  name: 'build_filter',
  description: `Translate a natural language query into a CRM filter. Executes the filter, returns a preview — does NOT apply or save until the user confirms via show_filter_preview.
Supports multiple OR groups (groups are OR-ed; conditions within a group are AND-ed).

Rules:
- When the user says "my", "mine", "assigned to me", or similar: pass \`scopeToCurrentUser: true\` — do NOT call search_entities to resolve "me".
- Call get_schema first to get exact field names. Use only fields (and relations) returned by get_schema.
- For each condition provide the field name and the human-readable value (e.g. "Customer", "active", "2024-01-01"). Do NOT provide an operator — it is derived automatically from the field type.
- For date fields use relative expressions where possible: { amount, unit } for inLast/inNext.
- Filtering by a related record's ATTRIBUTE — "records that HAVE a related record whose field = value" (e.g. "companies that have an activity of type call", "persons with an activity of subType email opened"): use the dotted path "<relationPath>.<attribute>" (e.g. "activities.type", "activities.subType") with the human-readable label as the value (e.g. "Phone call", "Email opened"). This is a condition on the related records' fields — do NOT call search_entities and do NOT pass an id. This is the correct path for "of type X" / "of subType X" queries; never turn the type/subType label into a record id.
- Filtering by a related record's RECENCY — "records with recent activity", "active in the last N days": filter on a DATE field of the relation, "<relationPath>.<dateField>" (e.g. "activities.beginAt", "activities.updatedAt"), with a date range. A relation name on its own ("activities", "tickets") is NOT a filterable field — never emit a bare relation path as a condition field. Filtering for the ABSENCE of a relation ("records that have NO activity", "no recent activity") is not supported: relation conditions match records that HAVE a matching related record, never the lack of one.
- Filtering by a SPECIFIC related RECORD the user named (a particular company, person, account manager): first call search_entities on the target class to resolve the name to its _id, then pass that _id as the value — never a name. Use the field path "<relationPath>.id" for collection relations (e.g. "companies.id") and the reference field name for single references (e.g. "accountManager"), with op "in". Only use ".id" when the user named an actual record; a type/subType label is never a record id. NOTE: a single m:1 reference to a PARENT record (e.g. Opportunity/Quote/Order "customer", an "account") is NOT filterable server-side on every entity — for "records belonging to a named company/person" (e.g. "opportunities for company X") use get_related from that parent, not a "customer in [id]" filter.
- Filtering on a single m:1 reference's OWN field uses a dotted path (e.g. "accountManager.salutation"). The valid nested fields are those of the reference's target class; if build_filter reports a field is not searchable, pick one from the valid list it returns.
- Call build_filter ONCE per user query. After it returns (success or corrected retry), immediately call show_filter_preview — pass the matchCount from THIS call verbatim (null is normal; do NOT use a matchCount from a previous build_filter call). Only call build_filter again if it explicitly returned an error — do not retry on a valid result, and do not narrate retries to the user.`,
  concurrencySafe: true,
  cacheable: false,
  inputSchema: () =>
    z.object({
      entityType: z
        .enum(Object.keys(getEntityRegistry()) as [string, ...string[]])
        .describe('Which entity type to filter'),
      groups: z
        .array(
          z.object({
            conditions: z
              .array(
                z.object({
                  field: z
                    .string()
                    .describe(
                      'Exact field name from get_schema, e.g. "status"',
                    ),
                  op: z
                    .string()
                    .describe(
                      'Operator from FIELD_TYPE_OPERATORS for this field type, e.g. "in", "eq", "gte". Use "in" for enum/reference when selecting multiple values.',
                    ),
                  value: z
                    .unknown()
                    .describe(
                      'Human-readable value, e.g. "Customer", "active", "2024-01-01", or { amount: 7, unit: "days" } for relative dates. Unit must be plural: "days", "weeks", "months", "years".',
                    ),
                }),
              )
              // Every OR group must carry at least one condition — an empty group
              // is meaningless and would produce a degenerate `{and:[]}`.
              .min(1),
          }),
        )
        .optional()
        .describe(
          "Groups are OR-ed together. Conditions within a group are AND-ed. Required unless you pass baseSegmentId with no new conditions to load and apply an existing segment's filter unchanged — in that case omit groups.",
        ),
      description: z
        .string()
        .describe('Human-readable description of what this filter selects'),
      baseSegmentId: z
        .string()
        .optional()
        .describe(
          "When REFINING an existing segment by ADDING/NARROWING conditions, pass that segment (List) _id here and put ONLY the new conditions in `groups`. build_filter loads the segment's stored filter and AND-combines your new conditions into it, so nothing already in the filter is lost. To APPLY a segment's stored filter to the entity list unchanged (e.g. \"apply this segment's filter to the companies list\"), pass baseSegmentId and OMIT groups entirely — build_filter returns the segment's filter as-is; then call show_filter_preview to apply it. Omit baseSegmentId when creating a filter from scratch or replacing a segment's filter entirely.",
        ),
      scopeToCurrentUser: z
        .boolean()
        .optional()
        .describe(
          "When the user asks for 'my X', 'mine', or 'assigned to me', pass true instead of calling search_entities to resolve 'me'. Injects the current user's id on the entity's user-scope field (e.g. agent for Activity) into every OR group.",
        ),
    }),

  async run(args, ctx) {
    const { data } = getHostRuntime()
    const { entityType, description, baseSegmentId, scopeToCurrentUser } = args
    // Extract-only mode: no groups + baseSegmentId loads the segment's stored filter
    // unchanged (so it can be applied to the list). Any other empty-groups call is a
    // model error — a filter with no conditions is meaningless.
    const groups = args.groups ?? []
    if (groups.length === 0 && !baseSegmentId) {
      return {
        error:
          "Provide at least one filter group, or a baseSegmentId to load an existing segment's stored filter.",
      }
    }

    const dc = resolveDataClass(entityType)
    if (!dc) {
      return unknownEntityError(entityType)
    }

    // Load schema once — drives op selection, enum resolution, and label maps.
    // Also capture the entity's filterable relation paths (e.g. "companies",
    // "activities") so relation-record conditions ("<relationPath>.id in [ids]")
    // are recognized instead of being treated as unknown fields.
    let schemaAttributes: SchemaAttributeMap
    let relationPaths = new Set<string>()
    // relationPath → target class name (e.g. "companies" → "Company"), so a
    // relation-attribute filter ("companies.group") can resolve its leaf enum
    // against the related entity's schema — not just the ".id" record select.
    const relationTargets = new Map<string, string>()
    try {
      const result = await data.getSchema(data.getSchemaName(entityType))
      schemaAttributes = result.schemaAttributes
      const rawRelations = result.schema?.get('relations')
      if (Array.isArray(rawRelations)) {
        // schema.get returns unknown; narrowed to AdapterSchemaRelation[] after the
        // Array.isArray guard (same pattern as schema-cache.ts).
        relationPaths = new Set(
          (rawRelations as AdapterSchemaRelation[]).map((rel) => {
            // entityName is the lowercase target ("company"); capitalize to the
            // class name getSchemaName expects ("Company").
            relationTargets.set(
              rel.relationPath,
              rel.entityName.charAt(0).toUpperCase() + rel.entityName.slice(1),
            )
            return rel.relationPath
          }),
        )
      }
    } catch {
      return { error: `Failed to load schema for ${entityType}` }
    }

    if (!schemaAttributes) {
      return { error: `No schema found for ${entityType}` }
    }

    // Pre-resolve target schemas for nested reference fields (e.g.
    // "accountManager.salutation" → Contact's schema), once per reference field,
    // so the per-condition mapping below stays synchronous. Collect the distinct
    // prefix→className pairs first, then load the schemas in parallel — a filter
    // may touch several distinct nested references and each load is an independent
    // fetch.
    const nestedSchemas = new Map<string, NonNullable<SchemaAttributeMap>>()
    const nestedClassNames = new Map<string, string>()
    for (const group of groups) {
      for (const cond of group.conditions) {
        const dot = cond.field.indexOf('.')
        if (dot === -1) {
          continue
        }
        const prefix = cond.field.slice(0, dot)
        const suffix = cond.field.slice(dot + 1)
        if (nestedClassNames.has(prefix)) {
          continue
        }
        // Relation-attribute filter ("companies.group"): resolve the relation's
        // target class so the leaf enum/type is governed by that schema. The
        // ".id" record-select path needs no schema and is handled in the mapping.
        if (relationPaths.has(prefix)) {
          if (suffix !== 'id') {
            const target = relationTargets.get(prefix)
            if (target) {
              nestedClassNames.set(prefix, target)
            }
          }
          continue
        }
        const refAttr = schemaAttributes[prefix]
        if (refAttr?.type !== 'reference' || !refAttr.referenceTo) {
          continue
        }
        // Use the reference's DECLARED target (e.g. "Contact"), not the concrete
        // subtype it resolves to ("Person"). PISA validates a nested reference
        // filter against the declared class and rejects any attribute not on it
        // as "not searchable"; the manual FilterBuilder offers nested fields from
        // this same class (buildAvailableEntities.ts). Resolving to Person would
        // let the model filter on Person-only fields (communicationLanguage) that
        // exist in the schema but PISA 422s.
        nestedClassNames.set(prefix, refAttr.referenceTo)
      }
    }
    await Promise.all(
      [...nestedClassNames].map(async ([prefix, className]) => {
        try {
          const nested = await data.getSchema(data.getSchemaName(className))
          if (nested.schemaAttributes) {
            nestedSchemas.set(prefix, nested.schemaAttributes)
          }
        } catch {
          // Unresolved nested schema — the field falls through as unknown below.
        }
      }),
    )

    // Parity with the manual FilterBuilder, which hides these secondary contact
    // fields from the filter UI (useFilterCondition.tsx) — a user cannot build a
    // filter on them manually, so neither should the bot. The check is on the leaf
    // attribute key (the nested field for a dotted path). attr.hidden is applied
    // there too, but the cached schema doesn't carry that flag — a minor known gap.
    const hiddenFields = new Set<string>()
    for (const group of groups) {
      for (const cond of group.conditions) {
        const dot = cond.field.indexOf('.')
        const leaf = dot === -1 ? cond.field : cond.field.slice(dot + 1)
        if (HIDDEN_FILTER_ATTRIBUTES.includes(leaf)) {
          hiddenFields.add(cond.field)
        }
      }
    }
    if (hiddenFields.size > 0) {
      return {
        error: `These fields are not available for filtering: ${[
          ...hiddenFields,
        ]
          .map((f) => `"${f}"`)
          .join(
            ', ',
          )}. The filter builder excludes them — choose a different field.`,
      }
    }

    // A nested reference field ("accountManager.communicationLanguage") is only
    // filterable if the leaf exists on the reference's declared target class
    // (loaded above). PISA rejects any other attribute as "not searchable", and
    // that 422 is swallowed by the best-effort count preview below — so catch it
    // here and return the valid fields rather than applying an un-runnable filter.
    // Relation-attribute paths ("activities.type") are validated by the count
    // call, not here. Skip prefixes whose nested schema failed to load.
    const invalidNested: string[] = []
    for (const group of groups) {
      for (const cond of group.conditions) {
        const dot = cond.field.indexOf('.')
        if (dot === -1) {
          continue
        }
        const prefix = cond.field.slice(0, dot)
        if (relationPaths.has(prefix) || !nestedClassNames.has(prefix)) {
          continue
        }
        const nested = nestedSchemas.get(prefix)
        const suffix = cond.field.slice(dot + 1)
        if (nested && !nested[suffix]) {
          const valid = Object.keys(nested)
            .sort()
            .map((f) => `"${f}"`)
            .join(', ')
          invalidNested.push(
            `"${cond.field}": "${suffix}" is not a filterable field on "${prefix}" (valid fields: ${valid})`,
          )
        }
      }
    }
    if (invalidNested.length > 0) {
      return {
        error: `These reference fields are not searchable: ${invalidNested.join('; ')}. Choose a valid field, or search the related record and filter by its id instead.`,
      }
    }

    // A relation path on its own ("activities", "tickets") is not a filterable
    // field. PISA can only match records that HAVE a related record satisfying a
    // condition on one of its ATTRIBUTES ("activities.beginAt", "activities.id"),
    // never the bare existence or absence of the relation. A bare relation condition
    // 422s ("not searchable") and that error is swallowed by the best-effort count
    // preview below — so without this guard the un-runnable filter is cached and
    // applied. Reject it here and point at the two supported paths.
    const bareRelations = new Set<string>()
    for (const group of groups) {
      for (const cond of group.conditions) {
        if (cond.field.indexOf('.') === -1 && relationPaths.has(cond.field)) {
          bareRelations.add(cond.field)
        }
      }
    }
    if (bareRelations.size > 0) {
      const rels = [...bareRelations]
      // Emit guidance per relation so a filter naming several bare relations
      // ("activities" AND "tickets") gets valid paths for each, not only the first.
      const guidance = rels
        .map(
          (rel) =>
            `for "${rel}", use a date attribute with a range (e.g. "${rel}.beginAt" or "${rel}.updatedAt"), "${rel}.id" with op "in" and a specific record _id to match a record, or "${rel}.id" with op "empty"/"notEmpty" to filter for absence/presence of any related record`,
        )
        .join('; ')
      return {
        error: `A relation cannot be filtered on its own: ${rels
          .map((f) => `"${f}"`)
          .join(
            ', ',
          )}. To filter by a related record: ${guidance}. Filtering for the ABSENCE of related records (records that have NONE of these relations) is not supported.`,
      }
    }

    // A readonly own attribute is computed/system-maintained and is NOT a
    // server-side filter — e.g. Opportunity `open` (readonly boolean). The
    // match-count preview below 422s on it, but that 422 is swallowed (matchCount
    // stays null) and the un-runnable filter is returned as if valid, so the bot
    // tells the user it "applied" a broken filter. Reject it here with a pointer to
    // the editable status field the user most likely meant ("open opportunities" =
    // `status` in the open groups). Dates are the one readonly type that IS
    // filterable (createdAt/updatedAt), so allow those. Schema-driven and portable
    // — matches list_entities' guard.
    const topLevelFields = new Set<string>()
    for (const group of groups) {
      for (const cond of group.conditions) {
        if (!cond.field.includes('.')) {
          topLevelFields.add(cond.field)
        }
      }
    }
    const readonlyFields = findReadonlyFilterFields(
      topLevelFields,
      schemaAttributes,
    )
    if (readonlyFields.length > 0) {
      const editableStatusField = pickEditableStatusField(schemaAttributes)
      return {
        error:
          `These fields are computed/readonly and cannot be used as a filter: ${readonlyFields
            .map((f) => `"${f}"`)
            .join(', ')}.` +
          (editableStatusField
            ? ` To filter by state (open, active, won, etc.), use the editable "${editableStatusField}" field with a specific value instead.`
            : ' Choose an editable field.'),
      }
    }

    const enumErrors: string[] = []

    // Reference-record conditions (accountManager, "<relationPath>.id", ...) carry raw
    // record ids. Collect them here while the schema context is in scope, then validate
    // below that each points to a live record — a guessed id must never be persisted.
    const referenceChecks: Array<{
      field: string
      ids: string[]
      referenceTo: string
      referenceFilter?: AdapterReferenceFilter
    }> = []
    const toIdList = (v: unknown): string[] =>
      (Array.isArray(v) ? v : [v]).filter(
        (x): x is string => typeof x === 'string' && x.length > 0,
      )

    const structure: SavedFilterStructure = {
      or: groups.map((g) => ({
        // Merge same-field in/nin conditions so "group is CST" + "group is LEA"
        // become one "group in [CST, LEA]" — an AND of both is a contradiction
        // for single-valued fields and PISA rejects the duplicate key (422).
        and: mergeSameFieldConditions(
          g.conditions.map((cond) => {
            const dot = cond.field.indexOf('.')
            const prefix = dot === -1 ? '' : cond.field.slice(0, dot)
            const suffix = dot === -1 ? '' : cond.field.slice(dot + 1)

            // Relation-record select ("<relationPath>.id in [ids]"): not a scalar
            // attribute, so resolve it against the entity's relation list. Operators
            // and value shape mirror a reference record (in/nin over id arrays).
            const isRelationRecord =
              suffix === 'id' && relationPaths.has(prefix)
            if (isRelationRecord) {
              const allowedOps = FIELD_TYPE_OPERATORS.reference ?? ['in']
              const op = allowedOps.includes(cond.op as never) ? cond.op : 'in'
              const opDef =
                OPERATOR_DEFINITIONS[op as keyof typeof OPERATOR_DEFINITIONS]
              if (!opDef?.requiresValue) {
                return { field: cond.field, op, value: null }
              }
              const value = normalizeValueForOperator(op, cond.value)
              const targetClass = relationTargets.get(prefix)
              if (targetClass) {
                referenceChecks.push({
                  field: cond.field,
                  ids: toIdList(value),
                  referenceTo: targetClass,
                })
              }
              return { field: cond.field, op, value }
            }

            // Own field, or a nested reference field ("accountManager.category")
            // whose governing attribute lives in the pre-resolved target schema.
            const attr =
              dot === -1
                ? schemaAttributes[cond.field]
                : nestedSchemas.get(prefix)?.[suffix]

            if (!attr) {
              // Unknown field — pass through so the error surfaces in count call
              return { field: cond.field, op: 'eq', value: cond.value }
            }

            const fieldType = attr.type
            const allowedOps = FIELD_TYPE_OPERATORS[fieldType] ?? ['eq']
            // Validate LLM-provided op against what the field type allows; fall back to default
            const op = allowedOps.includes(cond.op as never)
              ? cond.op
              : (allowedOps[0] ?? 'eq')
            const opDef =
              OPERATOR_DEFINITIONS[op as keyof typeof OPERATOR_DEFINITIONS]

            if (!opDef?.requiresValue) {
              return { field: cond.field, op, value: null }
            }

            // enum/reference: resolve display label → PISA codes, flip eq→in and
            // expand same-title codes (canonical logic shared with the manual builder).
            if (
              (fieldType === 'enum' || fieldType === 'reference') &&
              attr.enumValues?.length
            ) {
              const normalized = normalizeEnumCondition(
                op,
                cond.value,
                attr.enumValues,
              )

              // Detect entries that normalizeEnumCondition couldn't resolve —
              // they fall through as raw strings (not valid PISA codes).
              const validCodes = new Set(attr.enumValues.map((e) => e.value))
              const resolvedValues = Array.isArray(normalized.value)
                ? (normalized.value as unknown[])
                : normalized.value !== null
                  ? [normalized.value]
                  : []
              const unresolved = resolvedValues
                .map(String)
                .filter((v) => !validCodes.has(v))
              if (unresolved.length > 0) {
                const validLabels = [
                  ...new Set(attr.enumValues.map((e) => e.title)),
                ]
                // For each bad value, check whether another field recognizes it —
                // "Lead" fails on status but belongs to group, so suggest that.
                const suggestions = unresolved
                  .map((v) => {
                    const otherFields = findFieldsForEnumValue(
                      schemaAttributes,
                      v,
                      cond.field,
                    )
                    if (otherFields.length === 0) {
                      return null
                    }
                    const fieldList = otherFields
                      .map((f) => `"${f.field}" ("${f.title}")`)
                      .join(', ')
                    return `"${v}" is a valid value for ${fieldList} — the user may have meant that field instead of "${cond.field}".`
                  })
                  .filter((s): s is string => s !== null)

                enumErrors.push(
                  `Field "${cond.field}" ("${attr.title}"): ${unresolved.map((v) => `"${v}"`).join(', ')} did not match any enum value. Valid display labels: ${validLabels.map((l) => `"${l}"`).join(', ')}` +
                    (suggestions.length > 0
                      ? `\n${suggestions.join('\n')}`
                      : ''),
                )
              }

              return {
                field: cond.field,
                op: normalized.op,
                value: normalized.value,
              }
            }

            // Normalize inLast/inNext/notInLast/notInNext unit to plural (PISA requires "days" not "day")
            if (
              (op === 'inLast' ||
                op === 'inNext' ||
                op === 'notInLast' ||
                op === 'notInNext') &&
              cond.value !== null &&
              typeof cond.value === 'object' &&
              'unit' in (cond.value as object) &&
              'amount' in (cond.value as object)
            ) {
              // Both fields are guaranteed present by the guard above, so the
              // cast to the concrete relative-range shape is sound.
              const v = cond.value as { amount: number; unit: string }
              const unit = v.unit.endsWith('s') ? v.unit : `${v.unit}s`
              return { field: cond.field, op, value: { ...v, unit } }
            }

            // Scalar / reference-record fallthrough — shape the value to the
            // operator (lone id → [id] for in/nin), byte-identical to the manual
            // builder's normalizeValueForOperator.
            const value = normalizeValueForOperator(op, cond.value)
            // A single m:1 reference field (accountManager, ...) carries record ids
            // with no enumValues — flag them for the live-record check below.
            if (
              dot === -1 &&
              attr.type === 'reference' &&
              !attr.enumValues?.length &&
              attr.referenceTo
            ) {
              referenceChecks.push({
                field: cond.field,
                ids: toIdList(value),
                referenceTo: attr.referenceTo,
                referenceFilter: attr.referenceFilter,
              })
            }
            return { field: cond.field, op, value }
          }),
        ),
      })),
    }

    if (enumErrors.length > 0) {
      return {
        error: `Filter contains unresolvable enum values — do not save or show this filter. Fix the values and call build_filter again.\n\n${enumErrors.join('\n')}`,
      }
    }

    // Reject reference ids that resolve to no live record. A guessed or mis-copied id
    // would silently mis-count, leak the raw id onto the preview card (the display
    // resolver returns '' for a missing record), and break duplicate detection when a
    // sibling segment stored the record's real id. Same bar as the manual builder,
    // which resolves these exact ids through getReferenceDisplayValue.
    let unresolvedRefs: string[] = []
    try {
      const bad: string[] = []
      for (const check of referenceChecks) {
        const names = await data.resolveReferenceDisplayValues(
          check.ids,
          check.referenceTo,
          check.referenceFilter,
        )
        for (const id of check.ids) {
          if (!names[id]) {
            bad.push(`"${check.field}": "${id}"`)
          }
        }
      }
      unresolvedRefs = bad
    } catch {
      // Resolution backend unavailable (transient) — don't block a possibly-valid
      // filter; the card degrades to raw ids, matching the manual builder.
      unresolvedRefs = []
    }
    if (unresolvedRefs.length > 0) {
      return {
        error: `Filter references records that don't exist (a guessed or stale id): ${unresolvedRefs.join(', ')}. Look each up with search_entities and use the _id it returns — never guess or reuse an id.`,
      }
    }

    // Collapse single-condition OR groups on the same field+op into one unioned
    // group so "(group in [CST]) OR (group in [LEA])" becomes "group in [CST, LEA]"
    // — the canonical shape the manual builder produces.
    structure.or = mergeSameFieldGroups(structure.or)

    // Scope to current user: inject the entity's user-scope field (e.g. agent
    // for Activity) into every OR group. Trusted ctx data — skips the model
    // input pipeline and the reference-id live-record check.
    if (scopeToCurrentUser && ctx?.currentUserId) {
      const entityMeta = getEntityRegistry()[entityType]
      for (const scopeField of entityMeta?.userScopeFields ?? []) {
        for (const group of structure.or) {
          group.and.push({
            field: scopeField,
            op: 'in',
            value: [ctx.currentUserId],
          })
        }
      }
    }

    // Refining an existing segment: load its stored filter and AND-combine the new
    // conditions into it so nothing already in the filter is dropped. The stored
    // filter is already in this exact code-based shape (normalizeEnumCondition
    // accepts codes), so no re-resolution is needed. If the segment has no stored
    // filter yet, the delta stands alone.
    if (baseSegmentId) {
      const parsedBase = savedFilterStructureSchema.safeParse(
        await data.getStoredSegmentFilter(baseSegmentId),
      )
      if (parsedBase.success) {
        // No new conditions → apply the segment's stored filter unchanged. With new
        // conditions → AND-combine so nothing already in the filter is dropped.
        structure.or =
          groups.length === 0
            ? parsedBase.data.or
            : andCombineFilters(parsedBase.data.or, structure.or)
      } else if (groups.length === 0) {
        // Extract-only against a segment that has no stored filter yet: there is
        // nothing to apply. Fail loud rather than applying a match-all filter.
        return {
          error:
            'This segment has no stored filter to apply. Refine it first, then try again.',
        }
      }
    }

    // Match-count preview via the structured `?query=` channel — the exact call the
    // list page makes on Apply. EntityListContext passes `filters: { query }`, which
    // Scrivito serializes to the `?query=` URL param; counting the same way covers own
    // fields, multi-value enum `in`, relations, and references identically, so the
    // previewed number always matches the applied list. Best-effort: a transient
    // failure leaves it null rather than aborting a valid filter.
    let matchCount: number | null = null
    try {
      matchCount = await data.countResolved(dc, {
        encodedFilterQuery: encodeFilterQuery(structure),
      })
    } catch (err) {
      // A top-level m:1 reference condition (e.g. Opportunity `customer in [id]`)
      // is the one un-runnable class the guards above cannot catch: the field is
      // in the schema and the id is live, but PISA rejects filtering that
      // reference server-side on several entities. The count call — the exact
      // query the list page will run on Apply — is where that rejection first
      // surfaces. Unlike a transient blip it can never succeed, so returning the
      // filter would apply an un-runnable query and show the user a broken list.
      // When the preview fails AND the filter carries such a reference condition,
      // reject it and route the model to get_related (relation traversal from the
      // parent record, which always works). Without one, keep the best-effort
      // null (a genuine transient failure must not abort a valid filter).
      const unfilterableRefs = referenceChecks
        .filter((c) => !c.field.includes('.'))
        .map((c) => c.field)
      if (unfilterableRefs.length > 0) {
        return {
          error: `${entityType} cannot be filtered by the reference field(s) ${unfilterableRefs
            .map((f) => `"${f}"`)
            .join(
              ', ',
            )} — the backend rejects it. Do NOT retry build_filter for this. To find ${entityType} records belonging to a specific company or person, use get_related from that parent record instead (e.g. the company's related ${entityType.toLowerCase()}).`,
        }
      }
      // A 422 validation rejection is the backend refusing the query — it can
      // NEVER succeed, so returning the filter would let show_filter_preview
      // navigate and claim "applied" while the list page runs the same rejected
      // query and shows nothing. This is exactly what happens when the advanced
      // `?query=` channel is unavailable on an instance: even a well-formed
      // `status in [...]` filter 422s (DataScopeError "A validation has failed").
      // Scrivito wraps the ClientError into a DataScopeError that drops the HTTP
      // status, so the validation message is the only signal left — match it. A
      // transient DataConnectionError carries a different message and falls through
      // to the best-effort null below (a genuine blip must not abort a valid
      // filter). Rather than dead-ending the user with an internal "advanced
      // filtering unavailable" message, route the model to list_entities, which
      // retrieves the same records via a flat-channel fallback that works on this
      // instance — so the answer matches a healthy instance instead of apologizing.
      if (err instanceof Error && /validation has failed/i.test(err.message)) {
        return {
          error:
            `This instance cannot apply a structured filter to the ${entityType} list page (the backend rejected the query). Do NOT retry build_filter and do NOT navigate. ` +
            `Instead call list_entities for ${entityType} with the same criteria (resolve a state like "open" to its status values) to retrieve the matching records and present them to the user in chat — list_entities handles this instance's limitation automatically. ` +
            'Do NOT mention "advanced filtering" or this limitation to the user; just answer with the records.',
        }
      }
      // Leave the count null (rendered as "unknown", never as a real 0) rather
      // than abort a valid filter, but warn so a persistent failure is
      // diagnosable instead of silent. Same best-effort pattern as get_related.
      console.warn('[build_filter] match-count preview failed:', err)
      matchCount = null
    }

    // Cache the exact structure so the create/dedup tools read it back verbatim
    // rather than the model relaying (and drifting) a copy across turns.
    cacheBuiltFilter(entityType, structure)

    // No label maps here — show_filter_preview builds them from the schema itself.
    // Returning them would only invite the model to relay a copy back.
    return {
      structure,
      matchCount,
      description,
      entityType,
    }
  },
})
